import { CalendarRange, Zap } from 'lucide-react';
import SovereignDatePicker from '../components/SovereignDatePicker';
import TimelineGraph from '../components/TimelineGraph';
import { HelperText, SectionHeading, svInput } from '../ui';

function CapacityTile({
  enabled, label, unit, value, setValue, accent, accentSoft, disabledHint, warn,
}: {
  enabled: boolean; label: string; unit: string; value: string; setValue: (v: string) => void;
  accent: string; accentSoft: string; disabledHint: string; warn?: string;
}) {
  if (!enabled) {
    return (
      <div className="rounded-xl px-4 py-5" style={{ border: '1px dashed rgba(11,59,46,0.25)', background: 'rgba(250,247,238,0.6)' }}>
        <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#7C8A82]">
          <Zap className="h-3.5 w-3.5" />
          {label}
        </p>
        <p className="mt-2 text-xs text-[#7C8A82]">{disabledHint}</p>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-xl px-3.5 py-3" style={{ border: '1px solid rgba(11,59,46,0.22)', borderLeftColor: accent, borderLeftWidth: 4, background: accentSoft }}>
      <p className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: accent }}>
        <Zap className="h-3.5 w-3.5" />
        {label}
      </p>
      <div className="mt-2 flex items-baseline gap-2">
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          min={0}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={`${svInput} font-mono text-base font-bold tabular-nums`}
          style={warn ? { borderColor: '#8A5A08' } : undefined}
          placeholder="0.00"
        />
        <span className="text-xs font-bold text-[#7C8A82]">{unit}</span>
      </div>
      {warn ? <HelperText tone="warn">{warn}</HelperText> : null}
    </div>
  );
}

export default function TimelineStep(props: {
  showDcMw: boolean;
  showAcMw: boolean;
  dcMw: string;
  setDcMw: (v: string) => void;
  acMw: string;
  setAcMw: (v: string) => void;
  acExceedsDc: boolean;
  startDate: string;
  setStartDate: (v: string) => void;
  endDatePlanned: string;
  setEndDatePlanned: (v: string) => void;
  commissioningDate: string;
  setCommissioningDate: (v: string) => void;
  durationDays: number | null;
  startEndInverted: boolean;
  accent: string;
}) {
  const {
    showDcMw, showAcMw, dcMw, setDcMw, acMw, setAcMw, acExceedsDc,
    startDate, setStartDate, endDatePlanned, setEndDatePlanned,
    commissioningDate, setCommissioningDate, durationDays, startEndInverted, accent,
  } = props;
  const noCapacity = !showDcMw && !showAcMw;

  return (
    <div className="space-y-7">
      <section>
        <SectionHeading icon={Zap} title="Capacity" subtitle="Nameplate values for this project type" />
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <CapacityTile enabled={showDcMw} label="DC capacity" unit="MW" value={dcMw} setValue={setDcMw} accent="#9A7D3C" accentSoft="rgba(154,125,60,0.12)" disabledHint="Applies to solar & hybrid projects" />
          <CapacityTile enabled={showAcMw} label="AC capacity" unit="MW" value={acMw} setValue={setAcMw} accent="#3F6B82" accentSoft="rgba(63,107,130,0.12)" disabledHint="Solar / wind / hybrid / BESS" warn={acExceedsDc ? 'AC should not exceed DC' : undefined} />
        </div>
        {noCapacity ? <HelperText>No capacity fields apply to this project type.</HelperText> : null}
      </section>

      <section>
        <SectionHeading icon={CalendarRange} title="Timeline" subtitle="All dates shown as MM-DD-YYYY" />
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          <SovereignDatePicker id="epc-start" label="Start date" required value={startDate} onChange={setStartDate} hint="e.g. term sheet signed" />
          <SovereignDatePicker id="epc-end" label="Planned end date" required value={endDatePlanned} onChange={setEndDatePlanned} minDate={startDate || undefined} warn={startEndInverted ? 'End must be on or after start' : undefined} />
          <SovereignDatePicker id="epc-comm" label="Commissioning date" value={commissioningDate} onChange={setCommissioningDate} hint="Grid sync / COD" minDate={startDate || undefined} />
        </div>
        <div className="mt-4">
          <TimelineGraph start={startDate} end={startEndInverted ? '' : endDatePlanned} commissioning={commissioningDate} accent={accent} />
        </div>
        {durationDays != null && durationDays > 730 ? (
          <HelperText tone="warn">Over 2 years — consider revising or splitting the project.</HelperText>
        ) : null}
        {durationDays != null && durationDays >= 0 && durationDays < 30 ? (
          <HelperText tone="warn">Less than a month feels short — confirm the planned end date.</HelperText>
        ) : null}
      </section>
    </div>
  );
}
